import { Box, Center, Flex, HStack } from "@chakra-ui/react";
import { isBefore, isSameDay, startOfDay } from "date-fns";
import { useMemo, useState } from "react";
import CDS from "design-system";
import { Meeting, MeetingType } from "features/Resources/Meeting/models";
import { PrefixId } from "features/Resources/Shared/models";
import { bucketMeetings, filterMeetingsByText, meetingReadiness } from "features/Resources/Meeting/helpers";
import NeedsActionCard from "./NeedsActionCard";
import MeetingsScheduleFilters from "./MeetingsScheduleFilters";
import PreviousSection from "./PreviousSection";
import DaySection from "./DaySection";

interface MeetingsScheduleViewProps {
  meetings: Meeting[] | undefined;
  onExpand: (prefixId: PrefixId) => void;
  onScheduleAll: () => void;
  startDate?: Date | string;
}

export default function MeetingsScheduleView({ meetings, onExpand, onScheduleAll, startDate }: MeetingsScheduleViewProps) {
  const [searchText, setSearchText] = useState("");
  const [activeTypes, setActiveTypes] = useState<Set<MeetingType>>(new Set());
  const [showSkipped, setShowSkipped] = useState(false);

  const today = useMemo(() => startOfDay(new Date()), []);

  // Needs-action always reflects the full set, independent of the schedule filters.
  const allBuckets = useMemo(() => bucketMeetings(meetings), [meetings]);
  const toFix = useMemo(
    () => allBuckets.days.flatMap((d) => d.scheduled.filter((m) => meetingReadiness(m).level === "attention")),
    [allBuckets],
  );

  const scheduleMeetings = useMemo(() => {
    let ms = filterMeetingsByText(meetings, searchText);
    if (activeTypes.size > 0) ms = ms.filter((m) => activeTypes.has(m.meetingType));
    return ms;
  }, [meetings, searchText, activeTypes]);

  const buckets = useMemo(() => bucketMeetings(scheduleMeetings), [scheduleMeetings]);
  const pastDays = useMemo(() => buckets.days.filter((d) => isBefore(d.date, today)), [buckets, today]);
  const upcomingDays = useMemo(() => buckets.days.filter((d) => !isBefore(d.date, today)), [buckets, today]);
  const sessionsTotal = useMemo(() => buckets.days.reduce((n, d) => n + d.scheduled.length, 0), [buckets]);
  const dayCount = buckets.days.length;

  const toggleType = (type: MeetingType) =>
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });

  if (allBuckets.needsScheduling.length === 0 && allBuckets.days.length === 0) {
    return (
      <Center py={12} w="100%">
        <CDS.Text14 color="fg.subtle">No meetings on this journey yet.</CDS.Text14>
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" gap={4}>
      <NeedsActionCard
        toFix={toFix}
        toSchedule={allBuckets.needsScheduling}
        onExpand={onExpand}
        onScheduleAll={onScheduleAll}
        startDate={startDate}
      />

      {allBuckets.days.length > 0 && (
        <Box>
          <HStack spacing={1.5} align="baseline" mb={3} px={1}>
            <CDS.Text18 fontWeight="bold">Meeting schedule</CDS.Text18>
            <CDS.Text14 color="fg.subtle">
              · {sessionsTotal} {sessionsTotal === 1 ? "meeting" : "meetings"} across {dayCount}{" "}
              {dayCount === 1 ? "day" : "days"}
            </CDS.Text14>
          </HStack>

          <Box mb={3}>
            <MeetingsScheduleFilters
              searchText={searchText}
              onSearchChange={setSearchText}
              activeTypes={activeTypes}
              onToggleType={toggleType}
              showSkipped={showSkipped}
              onToggleSkipped={() => setShowSkipped((v) => !v)}
            />
          </Box>

          {buckets.days.length > 0 ? (
            <CDS.Card padding="none" radius="large" elevation="small">
              <PreviousSection
                days={pastDays}
                onExpand={onExpand}
                startDate={startDate}
                showSkippedExpanded={showSkipped}
                defaultOpen={showSkipped || upcomingDays.length === 0}
              />
              {upcomingDays.map((day) => (
                <DaySection
                  key={day.key}
                  day={day}
                  onExpand={onExpand}
                  startDate={startDate}
                  isToday={isSameDay(day.date, today)}
                  showSkippedExpanded={showSkipped}
                />
              ))}
            </CDS.Card>
          ) : (
            <Center py={10} w="100%">
              <CDS.Text14 color="fg.subtle">No meetings match these filters.</CDS.Text14>
            </Center>
          )}
        </Box>
      )}
    </Flex>
  );
}
